import { Typography } from '@/components/atoms';
import { BackArrowIcon } from '@/components/atoms/BackArrowIcon';
import { ChevronRightIcon } from '@/components/atoms/icons';
import type { BreadcrumbItem } from '@/components/organisms/WebHeader';
import { useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { router } from 'expo-router';
import React from 'react';
import {
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { GUEST_PHONE_MAX } from '../constants/guestLayout';

export interface GuestBreadcrumbHeaderProps {
  readonly title: string;
  readonly breadcrumbs: ReadonlyArray<BreadcrumbItem>;
  readonly showBackButton?: boolean;
  readonly onBackPress?: () => void;
}

/**
 * Back + title + breadcrumb block for guest chrome (sits beside the logo).
 * Figma: title Size/2XL Medium; crumbs Size/S Regular (link underlined).
 */
export function GuestBreadcrumbHeader({
  title,
  breadcrumbs,
  showBackButton = false,
  onBackPress,
}: Readonly<GuestBreadcrumbHeaderProps>) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const { width } = useWindowDimensions();
  const isMobile = width < GUEST_PHONE_MAX;

  const handleBack = () => {
    if (onBackPress) {
      onBackPress();
      return;
    }
    router.back();
  };

  const handleBreadcrumbPress = (item: BreadcrumbItem) => {
    if (!item.path) {
      return;
    }
    if (item.replace) {
      router.replace(item.path as never);
      return;
    }
    router.push(item.path as never);
  };

  if (!title && breadcrumbs.length === 0 && !showBackButton) {
    return null;
  }

  return (
    <View style={styles.row}>
      {showBackButton ? (
        <Pressable
          onPress={handleBack}
          style={[
            styles.backButton,
            isMobile && styles.backButtonMobile,
            isRTL ? styles.backButtonRtl : styles.backButtonLtr,
          ]}
          accessibilityRole="button"
          accessibilityLabel={t('common.back')}
        >
          <BackArrowIcon
            size={isMobile ? 24 : 34}
            color={colors.fontPrimary}
          />
        </Pressable>
      ) : null}

      <View style={styles.center}>
        {title ? (
          <Typography
            variant="title2"
            color="primary"
            numberOfLines={1}
          >
            {title}
          </Typography>
        ) : null}

        {breadcrumbs.length > 0 ? (
          <View style={styles.breadcrumbs}>
            {breadcrumbs.map((item, index) => (
              <React.Fragment key={item.path ?? `bc-${item.label}-${index}`}>
                {index > 0 ? (
                  <View style={isRTL ? styles.iconFlip : undefined}>
                    <ChevronRightIcon
                      size={14}
                      color={colors.fontSecondary}
                    />
                  </View>
                ) : null}
                <Pressable
                  onPress={() => handleBreadcrumbPress(item)}
                  disabled={!item.path}
                  style={({ pressed }) => [
                    item.path && pressed ? styles.pressed : null,
                  ]}
                  accessibilityRole={item.path ? 'link' : 'text'}
                >
                  <Typography
                    variant="subhead"
                    color={item.path ? 'secondary' : 'primary'}
                    style={[
                      styles.breadcrumb,
                      item.path ? styles.breadcrumbLink : null,
                    ]}
                  >
                    {item.label}
                  </Typography>
                </Pressable>
              </React.Fragment>
            ))}
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  backButtonMobile: {
    paddingVertical: 4,
  },
  backButtonLtr: {
    marginRight: 12,
  },
  backButtonRtl: {
    marginLeft: 12,
  },
  iconFlip: {
    transform: [{ scaleX: -1 }],
  },
  center: {
    flex: 1,
    minWidth: 0,
    gap: 4,
    justifyContent: 'center',
  },
  breadcrumbs: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  /** Font/Size/S Regular, LH 100% (subhead is Medium — override to Regular) */
  breadcrumb: {
    fontSize: 14,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    lineHeight: 14,
    letterSpacing: 0,
  },
  breadcrumbLink: {
    textDecorationLine: 'underline',
  },
  pressed: {
    opacity: 0.7,
  },
});
